import { TextQnAGetters } from './getters';
import { TextQnAMutations } from './mutations';
import { emojisReaction, TextQNAPost, TextQNAQuestionDetails, TextQNAState } from './types';

import type { RootState } from '@/store';

import analytics from '@/analytics';
import { AGORA } from '@/constants';
import i18n from '@/i18n';
import NotificationService from '@/services/dialogs/notifications';
import { deletePost } from '@/services/event.service';
import loggerService from '@/services/logger.service';
import { handleSpeakerActivity, ActivityType } from '@/services/speakerActivity.service';
import {
  assignQuestionToSpeaker,
  createAPostInChannel,
  fetchChannelPosts,
  postAReaction,
  removeAReaction,
  resetTextQNAQuestionsMap,
  setTextQNAPosts,
  updateAPostInChannel,
  updateTextQADetails
} from '@/services/textQNA.service';

export interface TextQnAContext {
  state: TextQNAState;
  rootState: RootState;
  getters: TextQnAGetters;
  rootGetters: { [k: string]: any };
  commit: <k extends keyof TextQnAMutations>(mutation: k, payload?: any, config?: { root: boolean }) => any;
  dispatch: (action: string, payload?: any, root?: { root: boolean }) => Promise<any>;
}

const actions = {
  joinTextQnaChannel(context: TextQnAContext, textQNAChannel: string) {
    try {
      // preventing reloading if already loaded
      if (
        context.state.channel_loading[textQNAChannel] === 'loaded' &&
        context.state.textQnA?.detail?.questions?.length !== 0
      ) {
        return;
      }

      const currentEvent = context.rootGetters['event/currentEvent'];
      resetTextQNAQuestionsMap();
      context.commit('initTextQnA', {
        textQnaChannelID: textQNAChannel,
        isModerated: currentEvent?.is_text_qna_moderated || false,
        showAnswerersIdentity: currentEvent?.show_answerers_identity ?? true,
        answerersAlias: currentEvent?.textqna_answerers_alias || ''
      });
      context.commit('changeTextQnaState', 'loading');
      return context
        .dispatch('joinChannel', textQNAChannel)
        .then(() => {
          return context.dispatch('fetchInitialQNAs', textQNAChannel).then(() => {
            context.commit('changeTextQnaState', 'loaded');
            context.commit('setChannelStatus', {
              channelId: textQNAChannel,
              status: 'loaded'
            });
          });
        })
        .catch(err => {
          loggerService.warn('error in joinTextQnaChannel', err);
          context.commit('changeTextQnaState', 'initializationError');
          context.commit('setChannelStatus', {
            channelId: textQNAChannel,
            status: 'left'
          });
          throw err;
        });
    } catch (err) {
      loggerService.warn('Error joining text qna channel', err);
      throw err;
    }
  },
  fetchInitialQNAs(context: TextQnAContext, qnaChannelID: string) {
    const totalQNAQuestionsLimit = 3000;

    const loadPrevPostsForTextQNA = false;

    return fetchChannelPosts({
      channelId: qnaChannelID,
      noOfMessagesToLoad: totalQNAQuestionsLimit
    })
      .then(res => {
        if (!res.prev_post_id || !loadPrevPostsForTextQNA) {
          return Promise.resolve(res);
        }
        return fetchChannelPosts({
          channelId: qnaChannelID,
          noOfMessagesToLoad: totalQNAQuestionsLimit,
          referenceDirection: 'previous',
          referencePostID: res.prev_post_id
        })
          .then(prevPostResponse => {
            if (res.posts && prevPostResponse.posts) {
              const allPosts = {
                ...res.posts,
                ...prevPostResponse.posts
              };
              res.posts = allPosts;
              //@ts-ignore
              const allPostsOrder = [...res.order, ...prevPostResponse.order];
              res.order = allPostsOrder;
            }
            return res;
          })
          .catch(err => {
            loggerService.warn('error in fetchInitialQNAs', err);
          });
      })
      .then(res => {
        if (res?.posts) {
          const order: string[] =
            res.order && Array.isArray(res.order) && res.order.length ? res.order.slice().reverse() : [];
          const questions: TextQNAPost[] = order.map((postID: string): TextQNAPost => {
            const post = res.posts[postID];
            return getFormattedTextQNA(post);
          });
          context.commit('updateOlderPostPresentFlag', !!res.prev_post_id);
          setTextQNAPosts(questions);
          context.commit('changeTextQnaState', 'loaded');
        }
      })
      .catch(err => {
        loggerService.warn('error in fetchInitialQNAs', err);
        context.commit('changeTextQnaState', 'loadingError');
      });
  },
  joinChannel(context: TextQnAContext, channelId: string) {
    if (context.rootState.mattermost.joinedChannels.state === 'loading') {
      return new Promise((resolve, reject) => {
        loggerService.info('Joined channels are still loading! Retrying in 3sec');
        setTimeout(() => {
          context.dispatch('joinChannel', channelId).then(resolve).catch(reject);
        }, 3000);
      });
    }

    const userId = context.rootState?.user?.currentUser?.mmid;
    const alreadyJoined = !!context.rootState.mattermost.joinedChannels.value[channelId];
    if (userId && !alreadyJoined) {
      loggerService.info('Joining channels again because: ', alreadyJoined);
      context.commit('setChannelStatus', {
        channelId: channelId,
        status: 'loading'
      });
      return context
        .dispatch(
          'mattermost/makeMattermostCall',
          {
            url: `api/v4/channels/${channelId}/members`,
            method: 'POST',
            body: JSON.stringify({
              user_id: userId
            })
          },
          { root: true }
        )
        .then(res => {
          context.commit('setChannelStatus', {
            channelId: channelId,
            status: 'loaded'
          });
          context.rootState.mattermost.joinedChannels.value[channelId] = res;
          return res;
        })
        .catch(err => {
          loggerService.warn('Failed to join channel', channelId, err);
          context.commit('setChannelStatus', {
            channelId: channelId,
            status: 'left'
          });
          throw err;
        });
    }
    return context.rootState.mattermost.joinedChannels.value[channelId];
  },
  upvoteTextQnaQuestion(context: TextQnAContext, questionPostId: string) {
    try {
      if (context.state.textQnA?.qnaState === 'loading') {
        return;
      }

      const userId = (context.rootState as any).user.currentUser?.mmid;
      const textQnaQuestionDetails = context.getters['textQnaQuestions'];

      if (textQnaQuestionDetails.questions && Array.isArray(textQnaQuestionDetails.questions)) {
        const questionToUpvote = textQnaQuestionDetails.questions.find((ques: TextQNAQuestionDetails) => {
          return ques.id === questionPostId;
        });
        if (questionToUpvote && !questionToUpvote.upvotedByUser && !questionToUpvote.answered) {
          analytics.track('textQNA_upvoted', {
            eventId: context.rootState.route?.params?.eventID,
            broadcastId: context.rootState.route?.params?.id
          });
          questionToUpvote.totalUpvotes = questionToUpvote.totalUpvotes + 1;
          questionToUpvote.upvotedByUser = true;
          return postAReaction({
            postId: questionPostId,
            userId: userId,
            emojiName: emojisReaction.UPVOTED
          }).catch((err: any) => {
            questionToUpvote.totalUpvotes = questionToUpvote.totalUpvotes - 1;
            questionToUpvote.upvotedByUser = false;
            loggerService.error('error in upvoteTextQnaQuestion', err);
            context.commit('changeTextQnaState', 'loadingError');
          });
        }
      }
    } catch (err) {
      loggerService.error('error in upvoteTextQnaQuestion', err);
    }
  },
  postANewTextQNA(
    context: TextQnAContext,
    payload: {
      question: string;
      askAnonymously: boolean;
    }
  ) {
    if (context.state.textQnA) {
      createAPostInChannel({
        message: payload.question,
        channelId: context.state.textQnA.id,
        askAnonymously: payload.askAnonymously
      })
        .then(res => {
          if (context.rootGetters['event/isBroadcastSpeakerOrOrganizer']) {
            context.dispatch('approveTextQNAPost', res?.id);
          }
        })
        .catch(err => {
          loggerService.warn('Error occurred while posting a text qna question: ', err);
          context.commit('changeTextQnaState', 'loadingError');
        });
    }
  },

  assignTextQNAQuestion(context: TextQnAContext, payload: { questionId: string; assignedTo: string }) {
    if (!context.state.textQnA?.id) {
      return;
    }
    analytics.track('textQNA_assigned', {
      eventId: context.rootState.route?.params?.eventID,
      broadcastId: context.rootState.route?.params?.id,
      questionId: payload.questionId,
      assignedTo: payload.assignedTo
    });

    return assignQuestionToSpeaker({
      channelId: context.state.textQnA.id,
      rootId: payload.questionId,
      assignedTo: payload.assignedTo
    });
  },
  publishANewTextQNA(context: TextQnAContext, questionPostId: string) {
    if (context.state.textQnA) {
      analytics.track('textQNA_shared', {
        eventId: context.rootState.route?.params?.eventID,
        broadcastId: context.rootState.route?.params?.id
      });
      createAPostInChannel({
        props: {
          publishedPost: questionPostId
        },
        message: `Publishing Post with ID ${questionPostId}`,
        channelId: context.state.textQnA.id
      })
        .then(() => {
          handleSpeakerActivity(() => Promise.resolve(), ActivityType.QnAShare, ActivityType.QnAShareFailed);
        })
        .catch((err: any) => {
          context.commit('changeTextQnaState', 'loadingError');
          handleSpeakerActivity(() => Promise.reject(), ActivityType.QnAShare, ActivityType.QnAShareFailed);
        });
    }
  },
  unPublishCurrentTextQNA(context: TextQnAContext, questionPostId: string) {
    if (!context.state.textQnA?.detail?.publishedQuestion) {
      loggerService.warn('Text QnA: nothing to unpublish ');
      return;
    }
    createAPostInChannel({
      props: {
        publishedPost: 'NO_POST_PUBLISHED'
      },
      message: `Publishing Post with ID ${questionPostId}`,
      channelId: context.state.textQnA.id
    })
      .then(() => {
        handleSpeakerActivity(() => Promise.resolve(), ActivityType.QnAUnShare, ActivityType.QnAUnShareFailed);
      })
      .catch((err: any) => {
        context.commit('changeTextQnaState', 'loadingError');
        handleSpeakerActivity(() => Promise.reject(), ActivityType.QnAUnShare, ActivityType.QnAUnShareFailed);
      });
  },
  postTextQNAAnswer(
    context: TextQnAContext,
    payload: {
      answer: string;
      questionId: string;
    }
  ) {
    if (!context.state.textQnA) {
      return;
    }

    if (!context.state.textQnA.detail || !context.state.textQnA.id) {
      return;
    }

    if (context.state.textQnA.qnaState === 'loading') {
      return;
    }

    const currentRouteID = context.rootState.route.params.id;
    const isCurrentRouteBroadcast = context.rootState.route?.fullPath?.includes('/broadcast');

    if (!isCurrentRouteBroadcast || !currentRouteID) {
      return;
    }

    const currentEventUser = context.rootState.user.currentEventUser;

    const question = context.state.textQnA?.detail.questions.find(
      (question: TextQNAQuestionDetails) => question.id === payload.questionId
    );

    if (!question) {
      return;
    }
    const copyQuestion = JSON.parse(JSON.stringify(question));

    question.answered = true;
    question.answeredByUser = {
      id: currentEventUser?.id ?? '',
      name: currentEventUser?.name ?? '',
      mediumimage: currentEventUser?.mediumimage ?? '',
      thumbnail: currentEventUser?.thumbnail ?? '',
      is_in_person: currentEventUser?.eventUser?.is_in_person ?? (false as any)
    };

    if (question.answerId) {
      if (copyQuestion.answer === payload.answer) {
        return;
      }
      question.answer = payload.answer;
      analytics.track('textQNA_answer_edited', {
        eventId: context.rootState.route?.params?.eventID,
        broadcastId: context.rootState.route?.params?.id
      });

      updateAPostInChannel({
        message: payload.answer,
        channelId: context.state.textQnA.id,
        rootId: payload.questionId,
        postId: copyQuestion.answerId ?? ''
      })
        .then(() => {
          context.commit('changeTextQnaState', 'loaded');
        })
        .catch(err => {
          if (question) {
            question.answered = copyQuestion.answered;
            question.answerId = copyQuestion.answerId;
            question.answer = copyQuestion.answer;
            question.answeredByUser = copyQuestion.answeredByUser;
          }
          NotificationService.toast(
            i18n.global.t(`@@translate@@ Error`),
            i18n.global.t(`@@translate@@ Failed to update answer. Please try again!`),
            'error',
            null,
            AGORA.NOTIFICATION_TIMEOUT
          );
          loggerService.warn('Error occurred while updating answer ', err);
          context.commit('changeTextQnaState', 'loadingError');
        });
      return;
    }
    question.answer = payload.answer;
    analytics.track('textQNA_answered', {
      eventId: context.rootState.route?.params?.eventID,
      broadcastId: context.rootState.route?.params?.id
    });
    createAPostInChannel({
      message: payload.answer,
      channelId: context.state.textQnA.id,
      rootId: payload.questionId
    })
      .then(() => {
        context.commit('changeTextQnaState', 'loaded');
      })
      .catch(err => {
        if (question) {
          question.answered = false;
          question.answer = '';
          question.answeredByUser = {
            id: '',
            name: '',
            mediumimage: '',
            thumbnail: ''
          };
        }
        NotificationService.toast(
          i18n.global.t(`@@translate@@ Error`),
          i18n.global.t(`@@translate@@ Failed to post answer. Please try again!`),
          'error',
          null,
          AGORA.NOTIFICATION_TIMEOUT
        );
        loggerService.warn('Error occurred while posting answer ', err);
        context.commit('changeTextQnaState', 'loadingError');
      });
  },
  async deleteTextQNAPost(context: TextQnAContext, questionPostId: string) {
    const question = context.state.textQnA?.detail?.questions?.find(question => question.id === questionPostId);

    if (!question) {
      return;
    }

    try {
      question.props = { ...question.props, isDeleted: true };

      if (context.state.textQnA?.detail.questions) {
        context.state.textQnA.detail.questions = context.state.textQnA?.detail.questions.filter(
          (question: TextQNAQuestionDetails) => question.id !== questionPostId
        );
      }
      await deletePost(context.rootState.event.event?.id, [questionPostId]);
      context.commit('changeTextQnaState', 'loaded');
    } catch (error) {
      question.props = { ...question.props, isDeleted: false };
      updateTextQADetails();
      NotificationService.toast(
        i18n.global.t(`@@translate@@ Error`),
        i18n.global.t(`@@translate@@ Failed to delete question. Please try again!`),
        'error',
        null,
        AGORA.NOTIFICATION_TIMEOUT
      );
      loggerService.error('Error Occured while deleting Text QNA: ', error);
    }
  },
  async markTextQNAPostUnAnswered(context: TextQnAContext, payload: { questionId: string; answerId: string }) {
    const question = context.state.textQnA?.detail.questions.find(question => question.id === payload.questionId);

    if (!question) {
      return;
    }

    const copyQuestion = JSON.parse(JSON.stringify(question));
    const userId = (context.rootState as any).user.currentUser?.mmid;

    question.answered = false;

    if (payload.answerId) {
      try {
        question.answer = '';
        question.answerId = undefined;
        await deletePost(context.rootState.event.event?.id, [payload.answerId]);
        context.commit('changeTextQnaState', 'loaded');
      } catch (error) {
        question.answer = copyQuestion.answer;
        question.answered = copyQuestion.answered;
        question.answerId = copyQuestion.answerId;
        loggerService.error('error in markTextQNAPostUnAnswered', error);
        context.commit('changeTextQnaState', 'loadingError');
      }
      return;
    }
    removeAReaction({
      postId: payload.questionId,
      userId: userId,
      emojiName: emojisReaction.ANSWERED
    })
      .then(() => {
        context.commit('changeTextQnaState', 'loaded');
      })
      .catch((err: any) => {
        question.answered = true;
        loggerService.error('error in markTextQNAPostUnAnswered', err);
        context.commit('changeTextQnaState', 'loadingError');
      });
  },
  markTextQNAPriority(context: TextQnAContext, questionPostId: string) {
    analytics.track('textQNA_prioritized', {
      eventId: context.rootState.route?.params?.eventID,
      broadcastId: context.rootState.route?.params?.id,
      questionPostId
    });
    const userId = (context.rootState as any).user.currentUser?.mmid;
    let question: TextQNAQuestionDetails | undefined;

    if (context.state.textQnA?.detail) {
      question = context.state.textQnA.detail.questions.find(question => question.id === questionPostId);
      if (question) {
        question.isPrioritized = true;
      }
    }

    return postAReaction({
      postId: questionPostId,
      userId,
      emojiName: emojisReaction.PRIORITIZED,
      forcedSyncFlow: true
    })
      .then(() => {
        context.commit('changeTextQnaState', 'loaded');
      })
      .catch((err: any) => {
        if (question) {
          question.isPrioritized = false;
        }
        loggerService.error('error in markTextQNAPriority', err);
        context.commit('changeTextQnaState', 'loadingError');
      });
  },
  unmarkTextQNAPriority(context: TextQnAContext, questionPostId: string) {
    analytics.track('textQNA_unprioritized', {
      eventId: context.rootState.route?.params?.eventID,
      broadcastId: context.rootState.route?.params?.id,
      questionPostId
    });
    const userId = (context.rootState as any).user.currentUser?.mmid;
    let question: TextQNAQuestionDetails | undefined;

    if (context.state.textQnA?.detail) {
      question = context.state.textQnA.detail.questions.find(question => question.id === questionPostId);
      if (question) {
        question.isPrioritized = false;
      }
    }

    return removeAReaction({
      postId: questionPostId,
      userId: userId,
      emojiName: emojisReaction.PRIORITIZED
    })
      .then(() => {
        context.commit('changeTextQnaState', 'loaded');
      })
      .catch((err: any) => {
        if (question) {
          question.isPrioritized = true;
        }
        loggerService.error('error in unmarkTextQNAPriority', err);
        context.commit('changeTextQnaState', 'loadingError');
      });
  },
  approveTextQNAPost(context: TextQnAContext, questionPostId: string) {
    analytics.track('textQNA_approved', {
      eventId: context.rootState.route?.params?.eventID,
      broadcastId: context.rootState.route?.params?.id
    });
    const userId = (context.rootState as any).user.currentUser?.mmid;
    let question: TextQNAQuestionDetails | undefined;

    if (context.state.textQnA?.detail) {
      question = context.state.textQnA.detail.questions.find(question => question.id === questionPostId);
      if (question) {
        question.approvedByMods = true;
      }
    }
    return postAReaction({
      postId: questionPostId,
      userId: userId,
      emojiName: emojisReaction.APPROVED,
      forcedSyncFlow: true
    })
      .then(() => {
        context.commit('changeTextQnaState', 'loaded');
      })
      .catch((err: any) => {
        if (question) {
          question.approvedByMods = false;
        }
        loggerService.error('error in approveTextQNAPost', err);
        context.commit('changeTextQnaState', 'loadingError');
      });
  },
  markTextQNAPostAnswered(context: TextQnAContext, questionPostId: string) {
    analytics.track('mark_textQNA_answered', {
      eventId: context.rootState.route?.params?.eventID,
      broadcastId: context.rootState.route?.params?.id
    });
    const userId = (context.rootState as any).user.currentUser?.mmid;

    const question = context.state.textQnA?.detail.questions.find(question => question.id === questionPostId);

    if (question) {
      question.answered = true;
    }

    return postAReaction({
      postId: questionPostId,
      userId: userId,
      emojiName: emojisReaction.ANSWERED,
      forcedSyncFlow: true
    })
      .then(() => {
        context.commit('changeTextQnaState', 'loaded');
      })
      .catch((err: any) => {
        if (question) {
          question.answered = false;
        }
        loggerService.error('error in markTextQNAPostAnswered', err);
        context.commit('changeTextQnaState', 'loadingError');
      });
  },
  updateSortingMethod(context: TextQnAContext, sortingMode: any) {
    if (context.state.textQnA?.sortmode?.value === sortingMode?.value) {
      return;
    }
    context.commit('changeTextQNASortMode', sortingMode);
    updateTextQADetails();
  },
  updateFilteringMethod(context: TextQnAContext, filteringMode: any) {
    if (context.state.textQnA?.filtermode?.value === filteringMode?.value) {
      return;
    }
    context.commit('changeTextQNAFilterMode', filteringMode);
    updateTextQADetails();
  },
  updateSpeakerFilter(context: TextQnAContext, speakerFilter: any) {
    if (context.state.textQnA?.speakerFilter?.value === speakerFilter?.value) {
      return;
    }
    context.commit('changeTextQNASpeakerFilter', speakerFilter);
    updateTextQADetails();
  }
};

export default actions;

export function getFormattedTextQNA(postDetails: any): TextQNAPost {
  const postReactions = postDetails?.metadata?.reactions;
  return {
    id: postDetails?.id,
    text: postDetails?.message,
    createdAt: postDetails?.create_at,
    reactions: Array.isArray(postReactions) ? postReactions : [],
    updatedAt: postDetails?.update_at,
    mmidOfWhoPosted: postDetails?.user_id,
    root_id: postDetails?.root_id,
    props: postDetails?.props,
    identifiers: postDetails?.identifiers
  };
}
